import React, { useState } from 'react';
import { BiX, BiUser } from 'react-icons/bi';
import { BsBicycle } from 'react-icons/bs';
import { FaStar } from 'react-icons/fa6';
import { useAppDispatch } from '../../redux/hooks';
import { suspendRider, approveRider } from '../../redux/thunk';
import toast from 'react-hot-toast';

interface Rider {
  _id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  status: string;
  isApproved: boolean;
  role: string;
  walletInfo: {
    currentBalance: number;
  };
  bankDetails?: Array<{
    _id: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
    inUse: boolean;
  }>;
  createdAt: string;
  accountStatus: string;
}

interface RiderDetailsModalProps {
  rider: Rider;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const RiderDetailsModal: React.FC<RiderDetailsModalProps> = ({
  rider,
  isOpen,
  onClose,
  onUpdate
}) => {
  const dispatch = useAppDispatch();
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Mock data for demo - replace with real data
  const performanceData = {
    totalDeliveries: 1254,
    totalEarnings: 'N124,000.00',
    rating: 4.5,
    startDate: '11th July, 2022'
  };

  const accountDetails = {
    accountNumber: '1123446254',
    accountName: 'Sarah John',
    bank: 'UBA PLC'
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }

    setIsLoading(true);
    try {
      await dispatch(suspendRider({
        riderId: rider._id,
        reason: suspendReason
      })).unwrap();
      
      toast.success('Rider suspended successfully');
      setShowSuspendModal(false);
      setSuspendReason('');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to suspend rider');
      console.error('Suspension error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    setIsLoading(true);
    try {
      await dispatch(approveRider(rider._id)).unwrap();
      toast.success('Rider reactivated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to reactivate rider');
      console.error('Reactivation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600';
      case 'suspended':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-teal-600">Rider Details</h2>
            <p className="text-gray-500 text-sm mt-1">
              View detailed information about {rider.fullName}'s profile, performance, and status.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <BiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Rider Profile Section */}
          <div className="flex gap-6">
            <div className="w-32 h-32 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src="/api/placeholder/128/128"
                alt="Rider profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "https://jy0s2swu0k.ufs.sh/f/LKSiq2B5GRS8nqXTASCYrqb8EcQRiuAhmfJoHsDWN5U347eg";
                }}
              />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-gray-700 mb-1">Name:</h3>
                <p className="text-gray-900">{rider.fullName}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-1">Status:</h3>
                <span className={`font-medium ${getStatusColor(rider.status)}`}>
                  {rider.status}
                </span>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-1">Phone Number:</h3>
                <p className="text-gray-900">{rider.phoneNumber}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-1">Ratings:</h3>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-900">{performanceData.rating}</span>
                  <FaStar className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-gray-500">Based off 125 reviews</span>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-1">E-mail:</h3>
                <p className="text-teal-600">{rider.email}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700 mb-1">Vehicle:</h3>
                <div className="flex items-center gap-1">
                  <BsBicycle className="w-4 h-4 text-yellow-500" />
                  <span className="text-yellow-500">Bicycle</span>
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="font-medium text-gray-700 mb-2">Address:</h3>
            <p className="text-gray-600">No 4, Gbagada Shomolu, Shomolu District</p>
          </div>

          {/* Performance Section */}
          <div>
            <h3 className="font-medium text-gray-700 mb-4">Performance:</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Total Deliveries:</h4>
                <p className="text-2xl font-semibold text-gray-900">{performanceData.totalDeliveries}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Total Earnings:</h4>
                <p className="text-2xl font-semibold text-gray-900">{performanceData.totalEarnings}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Start Date:</h4>
                <p className="text-lg text-gray-900">{performanceData.startDate}</p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div>
            <h3 className="font-medium text-gray-700 mb-4">Account Details:</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Account Number:</h4>
                <p className="text-gray-900">{accountDetails.accountNumber}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Account Name:</h4>
                <p className="text-gray-900">{accountDetails.accountName}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Bank:</h4>
                <p className="text-gray-900">{accountDetails.bank}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t">
            {rider.status?.toLowerCase() === 'suspended' ? (
              <button
                onClick={handleReactivate}
                disabled={isLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Reactivating...' : 'Reactivate'}
              </button>
            ) : (
              <button
                onClick={() => setShowSuspendModal(true)}
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <BiUser className="w-4 h-4" />
                Suspend Rider
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>

        {/* Suspend Modal */}
        {showSuspendModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-red-600">Suspend Restaurant Account</h2>
                <button
                  onClick={() => setShowSuspendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <BiX className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                This will suspend {rider.fullName} from the platform. They will be notified and can appeal this decision.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  *Suspension Reason
                </label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Provide a reason for suspension"
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSuspendModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSuspend}
                  disabled={isLoading || !suspendReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Suspending...' : 'Suspend Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderDetailsModal;